import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";

import Header from "../../../components/Header";
import {
  STATUS_FILTERS,
  duplicatePromotion,
  errorMessage,
  listPromotions,
  setPromotionEnabled,
  typeLabel,
  valueLabel,
} from "./promotionApi";
import { StatusChip, usePromotionStyles } from "./promotionStyles";

const validityLabel = (row) => {
  if (!row.valid_from && !row.valid_until) return "No date limit";
  return `${row.valid_from || "Any time"} → ${row.valid_until || "No end"}`;
};

const HpcPromotionList = () => {
  const navigate = useNavigate();
  const styles = usePromotionStyles();
  const { colors, theme } = styles;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listPromotions({ status: status || undefined, q: query || undefined }));
    } catch (err) {
      toast.error(errorMessage(err, "Could not load promotions"));
    } finally {
      setLoading(false);
    }
  }, [status, query]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (row) => {
    const enable = row.status !== "ACTIVE";
    try {
      await setPromotionEnabled(row.id, enable);
      toast.success(enable ? "Promotion enabled" : "Promotion disabled");
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Could not change the promotion status"));
    }
  };

  const duplicate = async (row) => {
    try {
      const result = await duplicatePromotion(row.id);
      toast.success("Promotion duplicated as a draft");
      navigate(`/hpc/promotions/${result.id}/edit`);
    } catch (err) {
      toast.error(errorMessage(err, "Could not duplicate the promotion"));
    }
  };

  const actionSx = { minWidth: 0, px: 1, py: 0.25, fontSize: "0.78rem" };

  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1.2,
      minWidth: 170,
      renderCell: ({ value }) => (
        <Typography variant="body2" fontWeight={700} color={colors.greenAccent[300]} noWrap>
          {value}
        </Typography>
      ),
    },
    {
      field: "code",
      headerName: "Code",
      flex: 0.8,
      minWidth: 130,
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 700, color: colors.grey[100] }}>
          {value}
        </Typography>
      ),
    },
    {
      field: "promotion_type",
      headerName: "Type",
      flex: 1,
      minWidth: 190,
      renderCell: ({ row }) => (
        <Box lineHeight={1.25}>
          <Typography variant="body2" color={colors.grey[100]}>{valueLabel(row)}</Typography>
          <Typography variant="caption" color={colors.grey[300]}>{typeLabel(row.promotion_type)}</Typography>
        </Box>
      ),
    },
    {
      field: "validity",
      headerName: "Validity",
      flex: 1,
      minWidth: 190,
      valueGetter: (_value, row) => validityLabel(row),
    },
    {
      field: "effective_status",
      headerName: "Status",
      flex: 0.6,
      minWidth: 120,
      renderCell: ({ value }) => <StatusChip status={value} />,
    },
    {
      field: "redemptions",
      headerName: "Redemptions",
      flex: 0.9,
      minWidth: 170,
      sortable: false,
      renderCell: ({ row }) => (
        <Box lineHeight={1.25}>
          <Typography variant="body2" color={colors.grey[100]}>
            {row.redeemed_count || 0}
            {row.max_redemptions ? ` / ${row.max_redemptions}` : ""} redeemed
          </Typography>
          <Box display="flex" gap={0.5} alignItems="center" flexWrap="wrap">
            <Typography variant="caption" color={colors.grey[300]}>{row.pending_count || 0} pending</Typography>
            {row.reconciliation_count > 0 && (
              <Tooltip title="Payments that need a reconciliation decision">
                <Chip
                  size="small"
                  label={`${row.reconciliation_count} to check`}
                  sx={{ backgroundColor: "#e65100", color: "#ffffff", fontWeight: 700, height: 20 }}
                />
              </Tooltip>
            )}
          </Box>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.6,
      minWidth: 360,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" gap={0.75} alignItems="center" flexWrap="wrap" py={0.5}>
          <Button
            size="small"
            variant="contained"
            startIcon={<VisibilityOutlinedIcon />}
            sx={{ ...styles.primaryButtonSx, ...actionSx }}
            onClick={() => navigate(`/hpc/promotions/${row.id}`)}
          >
            View
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditOutlinedIcon />}
            sx={{ ...styles.secondaryButtonSx, ...actionSx }}
            onClick={() => navigate(`/hpc/promotions/${row.id}/edit`)}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={row.status === "ACTIVE" ? <BlockIcon /> : <CheckCircleOutlineIcon />}
            sx={{ ...(row.status === "ACTIVE" ? styles.dangerButtonSx : styles.successButtonSx), ...actionSx }}
            onClick={() => toggle(row)}
          >
            {row.status === "ACTIVE" ? "Disable" : "Enable"}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            sx={{ ...styles.neutralButtonSx, ...actionSx }}
            onClick={() => duplicate(row)}
          >
            Duplicate
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m={{ xs: "12px", sm: "16px", md: "20px" }} sx={styles.pageSx}>
      <Header title="Promotions" subtitle="Promotion codes for new HPC registrations" />

      <Box
        mt={2}
        mb={2}
        display="flex"
        gap={1.5}
        flexWrap="wrap"
        alignItems="center"
        flexDirection={{ xs: "column", sm: "row" }}
      >
        <TextField
          size="small"
          placeholder="Search by name or code"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: { xs: "100%", sm: 300 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.grey[300] }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ width: { xs: "100%", sm: 240 } }}>
          <InputLabel id="promotion-status-filter">Status</InputLabel>
          <Select
            labelId="promotion-status-filter"
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {STATUS_FILTERS.map((option) => (
              <MenuItem key={option.value || "all"} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box flexGrow={1} display={{ xs: "none", sm: "block" }} />
        <Box display="flex" gap={1} flexWrap="wrap" width={{ xs: "100%", sm: "auto" }}>
          <Button
            variant="outlined"
            startIcon={<AssessmentOutlinedIcon />}
            sx={{ ...styles.secondaryButtonSx, flex: { xs: 1, sm: "none" } }}
            onClick={() => navigate("/hpc/promotions/report")}
          >
            Report
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ ...styles.primaryButtonSx, flex: { xs: 1, sm: "none" } }}
            onClick={() => navigate("/hpc/promotions/new")}
          >
            New promotion
          </Button>
        </Box>
      </Box>

      <Box
        height={{ xs: "62vh", sm: "68vh", md: "72vh" }}
        minHeight={{ xs: 360, sm: 420 }}
        sx={{ ...styles.gridSx, overflowX: "auto" }}
      >
        <Box sx={{ minWidth: 1300, height: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            getRowHeight={() => "auto"}
            density={isMobile ? "compact" : "standard"}
            sx={{ "& .MuiDataGrid-cell": { py: 1 } }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default HpcPromotionList;
